import './ReviewSubmit.scss';
import Store from 'state/Store';
import MoveToStepButton from 'components/shared/MoveToStepButton/MoveToStepButton';
import { ReactComponent as PaperClip } from 'assets/paperclip.svg';
import { connect } from 'react-redux';
import { Content } from 'utils/Content';
import { getPersonalDetails } from 'state/personalDetails/PersonalDetailsSelectors';
import { getDeceasedDetails } from 'state/deceasedDetails/DeceasedDetailsSelectors';
import { getDeceasedDetailsEnabled } from 'state/deceasedDetailsEnabled/DeceasedDetailsEnabledSelectors';
import { setCanContinueTrue } from 'state/canContinue/CanContinueActions';
import React, {
  useEffect,
  useState
} from 'react';
import {
  formatAccountNum,
  formatDateFromDateSelector,
  formatPhoneNumber,
  formatSSN
} from 'utils/FormattingUtil';
import WithPermissions from 'components/shared/WithPermissions';
import { ReactComponent as Arrow } from 'assets/arrow.svg';
import {
  decreaseStep,
  increaseStep
} from 'state/steps/StepActions';
import {
  generateSubmitPayload,
  generateBEPPayload
} from 'utils/SubmitDeathNotificationUtils';
import {
  submitBusinessNotification,
  submitDeathNotification
} from 'utils/Rest';
import { getSubmitRequestStatus } from 'state/submitNotification/SubmitNotificationSelectors';
import { Spinner } from '@trp-beacon-ui-kit/react-communication';
import { Navigate } from 'react-router-dom';
import { lrnLifeEvents } from 'utils/LayoutUtils';
import { LFEBeaconButton } from 'components/shared/LFEBeaconComponents';

const ReviewSubmit = ({ deceasedDetails, personalDetails, deceasedDetailsEnabled, submitRequestStatus }) => {
  useEffect(()=>{
    Store.dispatch(setCanContinueTrue())
    return ()=>{
      Store.dispatch(setCanContinueTrue())
    }
  },[])

  const [submitIsLoading, setSubmitIsLoading] = useState(false);
  const [isSubmitError, setIsSubmitError] = useState(false);

  useEffect(() => {
    if(submitRequestStatus === 'REQUESTED') {
      setSubmitIsLoading(true)
    }

    if(submitRequestStatus && submitRequestStatus === 'RECEIVED') {
      setSubmitIsLoading(false);
      const bepPayload = generateBEPPayload(personalDetails);
      submitBusinessNotification(bepPayload);
      Store.dispatch(increaseStep());
    }

    if(submitRequestStatus === 'ERROR') {
      setIsSubmitError(true);
    }

    return () => {
      setSubmitIsLoading(false);
      setIsSubmitError(false);
    }

  }, [submitRequestStatus, personalDetails])

  const handleBackClick = () => {
    Store.dispatch(decreaseStep());
  }

  const handleSubmitClick = () => {
    const payload = generateSubmitPayload(deceasedDetails, personalDetails, deceasedDetailsEnabled);
    submitDeathNotification(payload);
  }

  if(isSubmitError) {
    return <Navigate replace
                     to={'/death-notification/error'}/>
  }

  return (
      <div className="review-detail-page">
        <div data-testid="reviewSubmitIntro"
             className="dn-page-text-intro">{Content.ReviewSubmitIntro}</div>
        <DeceasedDetailsReview deceasedDetails={deceasedDetails}
                               deceasedDetailsEnabled={deceasedDetailsEnabled}/>
        <PersonalDetailsReview personalDetails={personalDetails}/>
        <div className="review-submit-button-container">
          
          <LFEBeaconButton variant="secondary"
          // className="review-submit-secondary-button"
                           icon-align="left"
                           label="Back"
                           onBeaconClick={() => handleBackClick()}
                           scale="normal"
         >
          <Arrow />
        </LFEBeaconButton>
          {!submitIsLoading 
          ? <LFEBeaconButton variant="primary"
         // className="review-submit-primary-button"
                             label="Submit"
                             onBeaconClick={() => handleSubmitClick()}
                             scale="normal"
        >
        </LFEBeaconButton>
        :<div className="loader"><Spinner /></div>}
        </div>
        <span className="legal-reference-number"
              data-testid="death-notification-lrn"
        >{lrnLifeEvents}</span>
      </div>
  );
};

const ReviewDetailsValue = ({ title, data, isDeceasedDetails = false }) => {
  const testId =
    (isDeceasedDetails ? 'deceased-details' : 'personal-details') +
    '-' +
    title.toLowerCase().split(' ').join('-');

  return (
    <div data-testid={`review-data-${testId}`}
         className='review-data'>
      {data}
    </div>
  );
};

const ReviewDetailsBlock = ({ children, title }) => {
  return (
    <div className='review-block'>
      <div className='review-title'>{title}</div>
      {children}
    </div>
  );
};

const ReviewDetailsBlockName = ({ data, testid, className }) => (
  <span data-testid={testid}
        className={className}>
    {data}
  </span>
);

const ReviewDetailsValueWithPermissions = WithPermissions(ReviewDetailsValue);
const ReviewDetailsDeathCertificatePermissions = WithPermissions(ReviewDetailsBlockName);

const DeceasedDetailsReview = ({ deceasedDetails, deceasedDetailsEnabled })=>{
  const { dfName, dmName, dlName, dAddress:{ dAddressMilitary, dAddressOne, dAddressTwo, dAddressCity, dAddressState, dAddressZip }, dDOB, dSSN,dAccountNum, dDeathCert, dDOD, dSuffix } = deceasedDetails
  const { enableAddress, enableDOB, enableSSN, enableAccountNum, enableDeathCert } = deceasedDetailsEnabled
  return (
    <div className="deceased-details">
        <div className="review-heading">
          Deceased&apos;s details
        </div>
      <div className="review-subheading">
      Deceased&apos;s name
        <MoveToStepButton step={0} />
      </div>
      <div className="review-blocks-data">
        <ReviewDetailsBlock title="First name"
                            data={dfName}
                            isDeceasedDetails>
          <ReviewDetailsValueWithPermissions title="First name"
                                             data={dfName}
                                             isDeceasedDetails
                                             shouldMask
          />
        </ReviewDetailsBlock>
        {dmName && <ReviewDetailsBlock title="Middle name (optional)"
                                       data={dmName}
                                       isDeceasedDetails
        >
          <ReviewDetailsValueWithPermissions title="Middle name (optional)"
                                             data={dmName}
                                             isDeceasedDetails
                                             shouldMask
          />
        </ReviewDetailsBlock>}
        <ReviewDetailsBlock title="Last name"
                            data={dlName}
                            isDeceasedDetails>
          <ReviewDetailsValueWithPermissions title="Last name"
                                             data={dlName}
                                             isDeceasedDetails
                                             shouldMask
          />
        </ReviewDetailsBlock>
        {dSuffix && <ReviewDetailsBlock title="Suffix (optional)"
                                        data={dSuffix}
                                        isDeceasedDetails>
          <ReviewDetailsValueWithPermissions title="Suffix (optional)"
                                             data={dSuffix}
                                             isDeceasedDetails
                                             shouldMask
          />
        </ReviewDetailsBlock>}
      </div>
      <div className="review-subheading">
        Additional details
        <MoveToStepButton step={1} />
      </div>
      <div className="deceased-details-additional-info">
        {enableAddress && (
          <div className="review-dAddress">
            <div className="review-blocks-data">
              <ReviewDetailsBlock title="Address type"
                                  data={dAddressMilitary ? 'Military' : 'US'}
                                  isDeceasedDetails
              >
                <ReviewDetailsValueWithPermissions title="Address type"
                                                   data={dAddressMilitary ? 'Military' : 'US'}
                                                   isDeceasedDetails
                                                   shouldMask
                />
              </ReviewDetailsBlock>
            </div>
            <div className="review-blocks-data">
              <ReviewDetailsBlock title="Address 1"
                                  data={dAddressOne}
                                  isDeceasedDetails
              >
                  <ReviewDetailsValueWithPermissions title="Address 1"
                                                     data={dAddressOne}
                                                     isDeceasedDetails
                                                     shouldMask
                />
              </ReviewDetailsBlock>
              {dAddressTwo && <ReviewDetailsBlock title="Address 2 (optional)"
                                                  data={dAddressTwo}
                                                  isDeceasedDetails
              >
                <ReviewDetailsValueWithPermissions title="Address 2 (optional)"
                                                   data={dAddressTwo}
                                                   isDeceasedDetails
                                                   shouldMask
                />
              </ReviewDetailsBlock>}
            </div>
            <div className="review-blocks-data">
              <ReviewDetailsBlock title="City"
                                  data={dAddressCity}
                                  isDeceasedDetails
              >
                <ReviewDetailsValueWithPermissions title="City"
                                                   data={dAddressCity}
                                                   isDeceasedDetails
                                                   shouldMask
                />
              </ReviewDetailsBlock>
              <ReviewDetailsBlock title="State"
                                  data={dAddressState}
                                  isDeceasedDetails
              >
                <ReviewDetailsValueWithPermissions title="State"
                                                   data={dAddressState}
                                                   isDeceasedDetails
                                                   shouldMask
                />
              </ReviewDetailsBlock>
              <ReviewDetailsBlock title="Zip code"
                                  data={dAddressZip}
                                  isDeceasedDetails
              >
                <ReviewDetailsValueWithPermissions title="Zip code"
                                                   data={dAddressZip}
                                                   isDeceasedDetails
                                                   shouldMask
                />
              </ReviewDetailsBlock>
            </div>
          </div>
        )}
        <div className="review-other-data-group">
          <div className="review-blocks-data">
            {enableDOB && (
              <div className="review-dDOB">
                <ReviewDetailsBlock title="Date of birth"
                                    data={formatDateFromDateSelector(dDOB)}
                                    isDeceasedDetails
                >
                  <ReviewDetailsValueWithPermissions title="Date of birth"
                                                     data={formatDateFromDateSelector(dDOB)}
                                                     isDeceasedDetails
                                                     shouldMask
                  />
                </ReviewDetailsBlock>
              </div>
            )}
            {enableSSN && (
              <div className="review-dSSN">
                <ReviewDetailsBlock title="Social Security Number"
                                    data={formatSSN(dSSN)}
                                    isDeceasedDetails
                >
                  <ReviewDetailsValueWithPermissions title="Social Security Number"
                                                     data={formatSSN(dSSN)}
                                                     isDeceasedDetails
                                                     shouldMask
                  />
                </ReviewDetailsBlock>
              </div>
            )}

            {enableAccountNum && (
              <div className="review-AccountNum">
                <ReviewDetailsBlock title="Account number"
                                    data={dAccountNum}
                                    isDeceasedDetails
                >
                  <ReviewDetailsValueWithPermissions title="Account number"
                                                     data={formatAccountNum(dAccountNum)}
                                                     isDeceasedDetails
                                                     shouldMask
                  />
                </ReviewDetailsBlock>
              </div>
            )}
          </div>
        </div>
        {(enableDeathCert || dDOD !== '') && (
          <div>
            <div className="review-heading review-additional-space">
              Death certificate
              <MoveToStepButton step={2} />
            </div>
            {enableDeathCert && (
              <div className="death-cert-file-group">
                <span className="death-cert-svg-review">
                  <PaperClip />
                  <ReviewDetailsDeathCertificatePermissions data={dDeathCert.name}
                                                            testid="death-certificate-name"
                                                            className="death-cert-file-name-review"
                                                            shouldMask
                  />
                </span>
              </div>
            )}
            {dDOD !== '' && !enableDeathCert && (
              <div className="review-dDOD">
                <ReviewDetailsBlock title="Date of Death"
                                    data={formatDateFromDateSelector(dDOD)}
                                    isDeceasedDetails
                >
                  <ReviewDetailsValueWithPermissions title="Date of Death"
                                                     data={formatDateFromDateSelector(dDOD)}
                                                     isDeceasedDetails
                                                     shouldMask
                  />
                </ReviewDetailsBlock>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const PersonalDetailsReview = ({ personalDetails }) =>{
  const { pfName, pmName, plName, pPhoneNum, pEmail, pAddress:{ pAddressMilitary, pAddressOne, pAddressTwo, pAddressCity, pAddressState, pAddressZip }, pRelationToDeceased, pExecutor, pSuffix } = personalDetails
  return (
    <div className="personal-details">
      <div className="review-heading review-additional-space">
        Submitter details
        <MoveToStepButton step={3} />
      </div>
      <div className="review-blocks-data">
        <ReviewDetailsBlock title="First name"
                            data={pfName}>
          <ReviewDetailsValueWithPermissions title="First name"
                                             data={pfName}
                                             shouldMask
          />
        </ReviewDetailsBlock>
        {pmName && <ReviewDetailsBlock title="Middle name (optional)"
                                       data={pmName}>
          <ReviewDetailsValueWithPermissions title="Middle name (optional)"
                                             data={pmName}
                                             shouldMask
          />
        </ReviewDetailsBlock>}
        <ReviewDetailsBlock title="Last name"
                            data={plName}>
          <ReviewDetailsValueWithPermissions title="Last name"
                                             data={plName}
                                             shouldMask
          />
        </ReviewDetailsBlock>
        {pSuffix && <ReviewDetailsBlock title="Suffix (optional)"
                                        data={pSuffix}>
          <ReviewDetailsValueWithPermissions title="Suffix (optional)"
                                             data={pSuffix}
                                             shouldMask
          />
        </ReviewDetailsBlock>}
      </div>
      <div className="review-blocks-data">
        <ReviewDetailsBlock title="Phone number"
                            data={formatPhoneNumber(pPhoneNum)}
        >
          <ReviewDetailsValueWithPermissions title="Phone number"
                                             data={formatPhoneNumber(pPhoneNum)}
                                             shouldMask
          />
        </ReviewDetailsBlock>
        <ReviewDetailsBlock title="Email"
                            data={pEmail}>
          <ReviewDetailsValueWithPermissions title="Email"
                                             data={pEmail}
                                             shouldMask
          />
        </ReviewDetailsBlock>
      </div>
      <div className="review-dAddress">
        <div className="review-blocks-data">
          <ReviewDetailsBlock title="Address type"
                              data={pAddressMilitary ? 'Military' : 'US'}
          >
            <ReviewDetailsValueWithPermissions title="Address type"
                                               data={pAddressMilitary ? 'Military' : 'US'}
                                               shouldMask
            />
          </ReviewDetailsBlock>
        </div>
        <div className="review-blocks-data">
          <ReviewDetailsBlock title="Address 1"
                              data={pAddressOne}>
            <ReviewDetailsValueWithPermissions title="Address 1"
                                               data={pAddressOne}
                                               shouldMask
            />
          </ReviewDetailsBlock>
          {pAddressTwo && <ReviewDetailsBlock title="Address 2 (optional)"
                                              data={pAddressTwo}>
            <ReviewDetailsValueWithPermissions title="Address 2 (optional)"
                                               data={pAddressTwo}
                                               shouldMask
            />
          </ReviewDetailsBlock>}
        </div>
        <div className="review-blocks-data">
          <ReviewDetailsBlock title="City"
                              data={pAddressCity}>
            <ReviewDetailsValueWithPermissions title="City"
                                               data={pAddressCity}
                                               shouldMask
            />
          </ReviewDetailsBlock>
          <ReviewDetailsBlock title="State"
                              data={pAddressState}>
            <ReviewDetailsValueWithPermissions title="State"
                                               data={pAddressState}
                                               shouldMask
            />
          </ReviewDetailsBlock>
          <ReviewDetailsBlock title="Zip code"
                              data={pAddressZip}>
            <ReviewDetailsValueWithPermissions title="Zip code"
                                               data={pAddressZip}
                                               shouldMask
            />
          </ReviewDetailsBlock>
        </div>
      </div>
      <div className="review-blocks-data">
        <ReviewDetailsBlock title="Relationship to the deceased"
                            data={pRelationToDeceased}
        >
          <ReviewDetailsValueWithPermissions title="Relationship to the deceased"
                                             data={pRelationToDeceased}
                                             shouldMask
          />
        </ReviewDetailsBlock>
        <ReviewDetailsBlock title="Are you the executor?"
                            data={pExecutor}>
          <ReviewDetailsValueWithPermissions title="Are you the executor?"
                                             data={pExecutor}
                                             shouldMask
          />
        </ReviewDetailsBlock>
      </div>
    </div>
  );
}

const mapStateToProps = state => ({
  deceasedDetails: getDeceasedDetails(state),
  personalDetails: getPersonalDetails(state),
  deceasedDetailsEnabled: getDeceasedDetailsEnabled(state),
  submitRequestStatus: getSubmitRequestStatus(state)
});

export default connect(mapStateToProps)(ReviewSubmit);
